import { useAuth } from "@/hooks/useAuth";
import { projectKeys } from "@/hooks/useProjectsQuery";
import { activityKeys } from "@/hooks/useHomeActivity";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getBackendToken } from "@/lib/tokenCache";

const CRITICAL_DATA_TIMEOUT_MS = 15_000;

export function MsalAuthGuard({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isExchanging } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [criticalDataReady, setCriticalDataReady] = useState(false);

  // Reset loading gate when user logs out so re-login re-engages it
  useEffect(() => {
    if (!isAuthenticated) {
      setCriticalDataReady(false);
    }
  }, [isAuthenticated]);

  // Secondary auth check: handles mid-session token expiry.
  // Primary auth gate is _authenticated route's beforeLoad.
  // Double-check localStorage to avoid false redirects when React state
  // is momentarily out of sync (e.g. MSAL re-initialization).
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !getBackendToken()) {
      navigate({ to: "/login" });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Wait for critical queries (projects + activity) to settle before unblocking
  useEffect(() => {
    if (isExchanging || !isAuthenticated) return;

    const check = () => {
      const pState = qc.getQueryState(projectKeys.all);
      const aState = qc.getQueryState(activityKeys.all);
      const pDone = pState?.status === "success" || pState?.status === "error";
      const aDone = aState?.status === "success" || aState?.status === "error";
      if (pDone && aDone) {
        setCriticalDataReady(true);
        return true;
      }
      return false;
    };

    // May already be resolved (returning user with warm cache)
    if (check()) return;

    const unsub = qc.getQueryCache().subscribe(() => {
      if (check()) unsub();
    });
    const timeout = setTimeout(
      () => setCriticalDataReady(true),
      CRITICAL_DATA_TIMEOUT_MS,
    );
    return () => {
      unsub();
      clearTimeout(timeout);
    };
  }, [isExchanging, isAuthenticated, qc]);

  const showLoading =
    isLoading || (isAuthenticated && (isExchanging || !criticalDataReady));

  if (showLoading) {
    const message = isLoading
      ? "Initializing..."
      : isExchanging
        ? "Signing you in..."
        : "Loading your workspace...";
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-muted-foreground">{message}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
